import { z } from "zod";

import { PermissionDenialSchema } from "./permission-denial";
import { ChatStreamTokenUsageSchema, FinishReasonSchema } from "./stream-usage.types";

/**
 * Marks the end of one LLM call within a multi-turn agent loop. Emitted once per
 * intermediate turn — NEVER as the final event of a stream.
 *
 * A `step-end` is always followed by either another LLM turn or a `message-end`.
 * It is never emitted immediately before `message-end` at stream terminus
 * (single-turn streams from Anthropic and OpenAI adapters skip `step-end` entirely
 * and emit only `message-end`).
 *
 * `usage` carries running token totals as of this step. For Daytona streams, these are running
 * totals (not per-step deltas) — consumers OVERWRITE their local state on each `step-end`.
 * The tracing middleware writes these to `LLMGeneration` immediately (mid-stream upsert) via
 * `updateGenerationUsagePartial`. The terminal `completeGeneration` DB write on `message-end`
 * uses the authoritative final usage.
 *
 * `hasToolCalls` is the authoritative FE signal that this step emitted tool calls,
 * so consumers don't have to interpret provider-specific finish-reason strings.
 */
export const StepEndEventSchema = z.object({
  type: z.literal("step-end"),
  stepIndex: z.number(),
  finishReason: FinishReasonSchema.optional(),
  model: z.object({ provider: z.string(), modelId: z.string() }).optional(),
  hasToolCalls: z.boolean().optional(),
  usage: ChatStreamTokenUsageSchema.optional(),
  /** Daytona/Anthropic Agent SDK only: tool use parent ID for nested agent turns. */
  parentToolUseId: z.string().optional(),
  /** Daytona/Anthropic Agent SDK only: number of web search API calls in this turn. */
  webSearchRequests: z.number().int().nonnegative().optional(),
  /** Post-resolution model name used for this turn (adapter-resolved, not the catalog key). */
  resolvedModelName: z.string().optional(),
  /**
   * Adapter-accumulated total reasoning wall-clock duration across all thinking blocks
   * completed in this step. Daytona only — per-step total, not a cross-step session total.
   * Computed in the adapter; consumers MUST NOT re-accumulate from per-block thinking-end events.
   */
  totalReasoningDurationMs: z.number().int().nonnegative().optional(),
  /** Provider-assigned request ID for this turn (e.g. Anthropic req_... from _request_id). Daytona only. */
  providerRequestId: z.string().optional(),
  /** Anthropic BetaMessage.id (msg_...) for this turn. */
  messageId: z.string().optional()
});

/**
 * Single terminal event for the entire streaming exchange. Exactly one per stream.
 * Carries final aggregated usage (sum across all steps), the final step's
 * `finishReason`, and optional `responseId`.
 *
 * This is the ONLY event that triggers a `completeGeneration` DB write in the
 * tracing middleware. For Daytona streams, `usage` is always populated (from SDK ResultMessage
 * on clean end, or from the running accumulator on abort). If `usage` is absent, the
 * tracing middleware falls back to `state.usage` populated by prior `step-end` events.
 *
 * The frontend transitions the stream state to done and commits the final snapshot
 * on receiving this event.
 */
export const MessageEndEventSchema = z.object({
  type: z.literal("message-end"),
  usage: ChatStreamTokenUsageSchema.optional(),
  finishReason: FinishReasonSchema.optional(),
  responseId: z.string().optional(),
  /** Post-resolution model name used for this stream (adapter-resolved, not the catalog key). */
  resolvedModelName: z.string().optional(),
  /** Provider-assigned request/response ID (Anthropic HTTP `request-id` `req_…`, OpenAI Responses API `resp_…`). */
  providerRequestId: z.string().optional(),
  /**
   * Adapter-accumulated total reasoning wall-clock duration across ALL thinking blocks in the
   * entire stream. Computed in the adapter; consumers MUST NOT re-accumulate from per-block
   * thinking-end events. Zero if no thinking occurred. Absent on hard-abort paths where
   * message-end fires without a settled accumulator.
   */
  totalReasoningDurationMs: z.number().int().nonnegative().optional(),
  /**
   * DB UUID of the assistant message row written at the end of this stream.
   * Absent on pre-rollout streams and orchestrators that have not yet been updated to populate it.
   * When present, the client MUST swap the optimistic nanoid for this UUID so Map-keyed
   * deduplication in `useChatMessages` works correctly after refetch.
   */
  assistantMessageId: z.string().uuid().optional(),
  /**
   * DB UUID of the user message row written before the stream started.
   * Absent on pre-rollout streams and orchestrators that have not yet been updated to populate it.
   * When present, the client MUST swap the optimistic nanoid for this UUID so Map-keyed
   * deduplication in `useChatMessages` works correctly after refetch.
   */
  userMessageId: z.string().uuid().optional(),
  /**
   * Every tool call the SDK refused during this turn, so a turn that did nothing
   * because everything was blocked is distinguishable from one that did what was
   * asked — which `finishReason` alone cannot say, since such a turn ends clean.
   *
   * A FLOOR, NOT PROOF — `PermissionDenialSchema` carries the carve-outs. The
   * same schema is what the runner hop declares, so the two cannot drift.
   *
   * Absent on every non-Daytona provider, and on a turn that denied nothing.
   */
  permissionDenials: z.array(PermissionDenialSchema).optional(),
  /**
   * Why the turn ended, mirrored from the SDK's `terminal_reason`.
   *
   * A bare string rather than the SDK's growing `TerminalReason` union: a value
   * this schema had not been told about would otherwise reject the whole
   * message-end and cost the reader the turn's usage and finish reason with it.
   * Distinct from `finishReason`, which is the model's own stop reason.
   */
  terminalReason: z.string().optional()
});
